import React from 'react';
import R from 'res/R';
import { LightVariantImage, DarkVariantImage, DefaultVariant, ImageSource } from 'lib/components/types';
import classes from './index.module.scss';

/**
 * Props interface for the Image component
 * Extends HTML img attributes with custom source handling
 */
export type ImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> & {
	/** Additional CSS class names */
	className?: string;
	/**
	 * Image source - can be:
	 * - A predefined image key from the resource bundle
	 * - A URL starting with 'http' or 'https'
	 * - A data URL starting with 'data:'
	 * - A variant.filename format for themed images
	 */
	src: ImageSource | string;
};

/**
 * Image component
 * Renders an image with support for various source formats
 * Handles resource bundle images, URLs, and themed variants
 *
 * @param {ImageProps} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
const Image = ({ src, className, ...rest }: ImageProps) => {
	let image = '';

	// Handle resource bundle images (not URLs or data URIs)
	if (!src.startsWith('http') && !src.startsWith('data')) {
		// Check if the source is in variant.filename format
		const [variant, filename] = src.split('.');
		if (variant === 'dark') {
			// Dark theme variant
			image = R.images.dark[filename as DarkVariantImage];
		} else if (variant === 'light') {
			// Light theme variant
			image = R.images.light[filename as LightVariantImage];
		} else {
			// Default (no variant) - use the source directly as a key
			image = R.images.default[src as DefaultVariant];
		}

		return (
			<img
				src={image}
				// Add special border class for avatar images
				className={`${src === 'plAvatar' ? classes['image-avatar-border'] : ''} ${className}`}
				{...rest}
			/>
		);
	}

	// Handle direct URLs and data URIs
	return (
		<img
			src={src}
			// Add special border class for avatar images
			className={`${src === 'plAvatar' ? classes['image-avatar-border'] : ''} ${className}`}
			{...rest}
		/>
	);
};

export default Image;
